import { useTranslation } from '@silospot/i18n/react';
import type { VerdictKind } from '@silospot/shared';
import { toast } from '@silospot/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';
import { type ReactElement, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Badge, Btn, Card, CardHead, PageHeader, Verdict } from '../../components/cld';
import { Flag } from '../../components/location';
import { api } from '../../lib/api';
import { fmtDateTime } from '../../lib/datetime';
import { normalizeTrustedIp } from '../../lib/whitelist';
import { usePeriodRange } from '../../stores/period-store';

const KINDS: { kind: VerdictKind; key: string; tone: 'clean' | 'fraud' | 'suspect' | 'neutral' }[] =
  [
    { kind: 'real_client', key: 'realClient', tone: 'clean' },
    { kind: 'false_positive', key: 'falsePositive', tone: 'suspect' },
    { kind: 'confirmed_fraud', key: 'confirmedFraud', tone: 'fraud' },
    { kind: 'internal_test', key: 'internalTest', tone: 'neutral' },
  ];

export function ReviewPage(): ReactElement {
  const { t } = useTranslation('pages');
  const { t: ts } = useTranslation('shell');
  const { id = '' } = useParams();
  const range = usePeriodRange();
  const qc = useQueryClient();
  const [trustIp, setTrustIp] = useState('');
  const { data, isLoading } = useQuery({
    queryKey: ['cld-review', id, range],
    queryFn: () => api.getReview(id, range),
    enabled: !!id,
  });
  // A verdict carries the ENTITY ip (exact IPv4 / IPv6 /64) so a real_client decision
  // actually reaches the whitelist — the block/exclusion matchers key off verdicts.ip.
  const mutate = useMutation({
    mutationFn: (v: { visitId?: string; ip?: string; verdict: VerdictKind }) =>
      api.postVerdict(id, { visitId: v.visitId, ip: v.ip, verdict: v.verdict }),
    onSuccess: () => {
      toast.success(t('design:cld.done', { defaultValue: 'Done' }));
      void qc.invalidateQueries({ queryKey: ['cld-review', id] });
    },
  });
  const addTrusted = () => {
    const ip = normalizeTrustedIp(trustIp);
    if (!ip) {
      toast.error(t('review.trustInvalid'));
      return;
    }
    mutate.mutate({ ip, verdict: 'real_client' }, { onSuccess: () => setTrustIp('') });
  };

  const header = <PageHeader title={ts('nav.review')} sub={t('review.intro')} />;
  if (isLoading || !data) {
    return (
      <div>
        {header}
        <Card>
          <div className="h-64 animate-pulse rounded-lg bg-neutral-bg" />
        </Card>
      </div>
    );
  }
  const kindKey = (k: string) => KINDS.find((x) => x.kind === k)?.key ?? k;

  return (
    <div>
      {header}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1.6fr_1fr]">
        <Card>
          <CardHead title={t('review.toReview')} sub={`${data.queue.length}`} />
          {data.queue.length === 0 ? (
            <p className="py-10 text-center text-[13px] text-text-3">{t('review.empty')}</p>
          ) : (
            <div className="flex flex-col">
              {data.queue.map((e, i) => (
                <div
                  key={e.visitId}
                  className={`flex flex-wrap items-center gap-3 py-3 ${i < data.queue.length - 1 ? 'border-b border-[#f1f3f6]' : ''}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-[13px] font-medium text-text">
                      {t(`signal.${e.signal}`)}
                      <Verdict score={e.score} size="sm" />
                    </div>
                    <div className="truncate font-mono text-[11.5px] text-text-3">
                      {e.ip} · <Flag country={e.country} /> {e.country} · {e.page || '/'}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {KINDS.map((kd) => (
                      <button
                        key={kd.kind}
                        type="button"
                        disabled={mutate.isPending}
                        onClick={() =>
                          mutate.mutate({ visitId: e.visitId, ip: e.ip, verdict: kd.kind })
                        }
                        className="rounded-md border border-border-strong bg-surface px-2 py-1 text-[11.5px] font-medium text-[#3a4151] hover:bg-bg disabled:opacity-50"
                      >
                        {t(`review.${kd.key}`)}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
        <div className="flex flex-col gap-4">
          <Card>
            <CardHead title={t('review.addTrusted')} sub={t('review.addTrustedSub')} />
            <div className="flex flex-wrap items-center gap-2">
              <input
                value={trustIp}
                onChange={(e) => setTrustIp(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') addTrusted();
                }}
                spellCheck={false}
                placeholder={t('review.addTrustedPlaceholder')}
                className="min-w-0 flex-1 rounded-[9px] border border-border-strong bg-surface px-3 py-1.5 font-mono text-[13px] outline-none focus:border-primary"
              />
              <Btn
                icon={ShieldCheck}
                disabled={mutate.isPending || !trustIp.trim()}
                onClick={addTrusted}
              >
                {t('review.addTrustedBtn')}
              </Btn>
            </div>
          </Card>
          <Card>
            <CardHead title={t('review.recentVerdicts')} />
            {data.verdicts.length === 0 ? (
              <p className="py-8 text-center text-[13px] text-text-3">—</p>
            ) : (
              <div className="flex flex-col">
                {data.verdicts.map((v, i) => (
                  <div
                    key={v.id}
                    className={`flex items-center justify-between gap-2 py-2.5 ${i < data.verdicts.length - 1 ? 'border-b border-[#f1f3f6]' : ''}`}
                  >
                    <div className="min-w-0">
                      <Badge tone={KINDS.find((x) => x.kind === v.verdict)?.tone ?? 'neutral'}>
                        {t(`review.${kindKey(v.verdict)}`)}
                      </Badge>
                      <div className="mt-1 truncate font-mono text-[11px] text-text-3">
                        {v.ip ?? v.visitId?.slice(0, 8) ?? '—'}
                      </div>
                    </div>
                    <span className="shrink-0 text-[11px] text-text-3">
                      {fmtDateTime(v.createdAt)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
